import { Fragment, type ReactNode } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { acaoLabel, origemLabel } from '@/components/Trilha/trilha_labels';
import { detalheLegivel } from '@/components/Trilha/trilha_detalhe';
import { formatarDataHora } from '@/lib/trilha_format';
import {
  Blueprint,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

export type AmbienteTrilha = 'plataforma' | 'escritorio' | 'banco';

export type ColunaTrilha = 'data' | 'acao' | 'autor' | 'ambiente' | 'objeto' | 'origem';

/**
 * O evento como a TABELA precisa dele — o mínimo que as duas telas têm em // ux-neutrality:allow doc-comment
 * comum. O console traz mais campos (`objeto_tipo`, `objeto_id`) e continua // ux-neutrality:allow doc-comment
 * podendo passar o objeto inteiro: o que sobra não atrapalha. // ux-neutrality:allow doc-comment
 */
export interface TrilhaEventoView {
  id: string;
  fonte: string;
  acao: string;
  ocorrido_em: string;
  /** `null` quando o fato não tem autor humano (a medição nasce do serviço). */
  autor_email: string | null;
  ambiente: AmbienteTrilha;
  objeto_rotulo: string | null;
  detalhe: Record<string, unknown>;
}

interface TrilhaTabelaProps {
  /** `null` = ainda carregando; `[]` = recorte vazio. */
  eventos: TrilhaEventoView[] | null;
  erro: string | null;
  colunas: readonly ColunaTrilha[];
  /**
   * Só o console mostra a coluna Ambiente, e o rótulo é do dialeto dele (um // ux-neutrality:allow doc-comment
   * banco lá se chama "Cliente"). Por isso o texto vem de fora, em vez de a // ux-neutrality:allow doc-comment
   * tabela compartilhada carregar vocabulário de uma tela só. // ux-neutrality:allow doc-comment
   */
  ambienteLabel?: (ambiente: AmbienteTrilha) => string;
}

const _CABECALHOS: Record<ColunaTrilha, string> = {
  data: ptBR.trilha.colData,
  acao: ptBR.trilha.colAcao,
  autor: ptBR.trilha.colAutor,
  ambiente: ptBR.trilha.colAmbiente,
  objeto: ptBR.trilha.colObjeto,
  origem: ptBR.trilha.colOrigem,
};

type _Renderizador = (
  evento: TrilhaEventoView,
  ambienteLabel: TrilhaTabelaProps['ambienteLabel'],
) => ReactNode;

const _CELULAS: Record<ColunaTrilha, _Renderizador> = {
  data: (evento) => (
    <TableCell className="font-mono text-body-sm text-fg-muted">
      {formatarDataHora(evento.ocorrido_em)}
    </TableCell>
  ),
  acao: (evento) => {
    const detalhe = detalheLegivel(evento.acao, evento.detalhe);
    return (
      <TableCell className="text-fg" data-testid="trilha-acao">
        <span className="block">{acaoLabel(evento.acao)}</span>
        {detalhe === '' ? null : (
          <span className="block text-caption text-fg-muted">{detalhe}</span>
        )}
      </TableCell>
    );
  },
  autor: (evento) => (
    <TableCell className="text-fg-muted" data-testid="trilha-autor-celula">
      {evento.autor_email ?? ptBR.trilha.semAutor}
    </TableCell>
  ),
  ambiente: (evento, ambienteLabel) => (
    <TableCell className="text-fg-muted">
      {ambienteLabel === undefined ? evento.ambiente : ambienteLabel(evento.ambiente)}
    </TableCell>
  ),
  objeto: (evento) => (
    <TableCell className="text-fg-muted">
      {evento.objeto_rotulo ?? ptBR.trilha.valorAusente}
    </TableCell>
  ),
  origem: (evento) => (
    <TableCell className="text-fg-muted" data-testid="trilha-origem">
      {origemLabel(evento.fonte)}
    </TableCell>
  ),
};

/**
 * A tabela da trilha, sem I/O: recebe os eventos e os três estados. // ux-neutrality:allow doc-comment
 *
 * Existe para que o console da plataforma (E53.F7) e o módulo do banco // ux-neutrality:allow doc-comment
 * (E61.F4) mostrem a MESMA trilha pelo mesmo caminho — duas tabelas lendo a // ux-neutrality:allow doc-comment
 * mesma leitura divergem na primeira mudança. Quem busca, filtra e pagina é a // ux-neutrality:allow doc-comment
 * página; aqui só se apresenta. // ux-neutrality:allow doc-comment
 */
export function TrilhaTabela({ eventos, erro, colunas, ambienteLabel }: TrilhaTabelaProps) {
  if (erro !== null) {
    return (
      // A mensagem SUBSTITUI a tabela depois de uma falha assíncrona: sem
      // região viva, quem usa leitor de tela não sabe que a lista parou de
      // representar o filtro atual (F-E61.F4-03).
      <p role="alert" data-testid="trilha-erro" className="text-body-md text-danger-700">
        {erro}
      </p>
    );
  }
  if (eventos === null) {
    return (
      <p data-testid="trilha-carregando" className="text-body-md text-fg-muted">
        {ptBR.trilha.carregando}
      </p>
    );
  }
  if (eventos.length === 0) {
    return (
      <p data-testid="trilha-vazio" className="text-body-md text-fg-muted">
        {ptBR.trilha.vazio}
      </p>
    );
  }
  return (
    <Blueprint>
      <div className="overflow-x-auto [contain:layout]">
        <Table>
          <TableHeader>
            <TableRow>
              {colunas.map((coluna) => (
                <TableHead key={coluna}>{_CABECALHOS[coluna]}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {eventos.map((evento) => (
              <TableRow key={evento.id} className="align-top" data-testid="trilha-linha">
                {colunas.map((coluna) => (
                  <Fragment key={coluna}>{_CELULAS[coluna](evento, ambienteLabel)}</Fragment>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Blueprint>
  );
}

export default TrilhaTabela;
